/* eslint-disable @typescript-eslint/no-non-null-assertion */

import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ActionCRA from './ActionCRA';
import { Button, HrefLink, Modal } from '@bloomays-lib/ui.shared';
import RawWarningIcon from '@mui/icons-material/Warning';
import RawUploadFileIcon from '@mui/icons-material/UploadFile';
import RawInsertDriveFileIcon from '@mui/icons-material/AssignmentInd';
import RawVisibilityIcon from '@mui/icons-material/Visibility';
import Delete from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { ActivityDayDetails, ActivityDayType, IMission, Role } from '@bloomays-lib/types.shared';
import NPSMission from '../organisms/NPSMission';
import { FetchResult } from '@apollo/client';
import { ActivitySummary, UploadFileActivity } from '../../types/components';
import { Logger } from '../../services/serviceLogger';
import SummaryDaysWorked from '../organisms/SummaryDaysWorked';
import { calculPerDay } from '../../helpers/CRA';
import { isZeroCRAActivity } from '@bloomays-lib/utils.shared';
import { FormCRA } from './FormCRA';
import { convertActivityToEachDaysArray, isBefore } from '@bloomays-lib/utils.shared';
import isEmpty from 'lodash/fp/isEmpty';
import { useAuthStore } from '../../stores';

const logger = Logger('ActionBundle');

export type ActionBundleProps = {
  CRAEditing: boolean;
  CRACanSendInvite: boolean;
  CRAUploaded: boolean;
  CRASignSent: boolean;
  canUploadInvoices: boolean;
  canSeeInvoices: boolean;
  canDeleteInvoices: boolean;
  editFunc: () => void;
  sendFunc: (signerFullname: string) => void;
  cancelFunc: () => void;
  i: number;
  uploadCRA: UploadFileActivity;
  deleteInvoice: (activityRecordID: string) => Promise<void>;
  setModalUploadBill: () => void;
  CRAValidated?: boolean | null;
  activity: ActivitySummary;
  refetchActivities: () => void;
  mission: IMission;
  upsertFollowUp: (args: {
    selected?: number;
    comment?: string;
    collectDate: Date;
    activityId: string;
  }) => Promise<FetchResult<any> | undefined>;
  stateNPS: { loading: boolean };
  getNPS: any;
};

export const ActionBundle = ({
  stateNPS,
  CRAEditing = true,
  CRACanSendInvite = false,
  CRAUploaded = false,
  CRASignSent = false,
  canUploadInvoices,
  canSeeInvoices,
  editFunc,
  sendFunc,
  cancelFunc,
  i,
  uploadCRA,
  setModalUploadBill,
  CRAValidated,
  activity,
  refetchActivities,
  mission,
  upsertFollowUp,
  getNPS,
  deleteInvoice,
  canDeleteInvoices,
}: ActionBundleProps) => {
  const [selected, setSelected] = useState<number | undefined>();
  const [comment, setComment] = useState<string | undefined>();
  const [nbDaysDetails, setNbDaysDetails] = useState<
    { nbDaysDetails: ActivityDayDetails[]; hoursPerDays: number; month: string } | undefined
  >(undefined);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const { t } = useTranslation('bloomer');
  const auth = useAuthStore((state) => state.auth);
  const isAdmin = auth?.user?.roles?.includes(Role.Admin);
  const theme = useTheme();
  const [modalState, setModalState] = useState<{
    nps: { id: null | number | string; show: boolean };
    craActions: { id: null | string; show: boolean };
    loading: { id: null | number | string; show: boolean };
    errorBillingInfos: { show: boolean; fields: string[] };
  }>({
    nps: { id: null, show: false },
    craActions: { id: null, show: false },
    loading: { id: null, show: false },
    errorBillingInfos: { show: false, fields: [] },
  });

  const handleClose = () => {
    setModalState({ ...modalState, nps: { ...modalState.nps, show: false } });
  };
  const handleCloseAction = () => {
    setModalState({ ...modalState, craActions: { id: null, show: false } });
  };

  const handleUploadInvoice = () => {
    const errorFields: string[] = [];

    if (isEmpty(mission.bloomerBillingSociety)) {
      errorFields.push(t('missingBillingSociety'));
    } else {
      const billingSociety = mission.bloomerBillingSociety![0];

      const allowedSocietyStatus = ['SUE', 'SHUE', 'Auto Entrepreneur', 'EI'];
      if (billingSociety.status && allowedSocietyStatus.includes(billingSociety.status)) {
        return setModalUploadBill();
      }

      if (!billingSociety.KBIS) errorFields.push(t('missingKBIS'));
      if (!billingSociety.URSSAFVigilanceCertificate) errorFields.push(t('missingUrssafCertificate'));
      if (!billingSociety.URSSAFVCExpirationDate) errorFields.push(t('missingUrssafExpirationDate'));
      if (
        billingSociety.URSSAFVCExpirationDate &&
        !isBefore(new Date(), new Date(billingSociety.URSSAFVCExpirationDate))
      )
        errorFields.push(t('expiredUrssafCertificate'));
    }

    if (!isEmpty(errorFields)) {
      if (isAdmin) {
        setModalUploadBill();
      }
      // permet de l'afficher en second par dessus
      setTimeout(() => {
        setModalState({ ...modalState, errorBillingInfos: { show: true, fields: errorFields } });
      }, 0);
    } else {
      setModalUploadBill();
    }
  };

  const labelCRAButton = CRAEditing ? `${t('validateCRA')}` : `${t('cancelOrEditCRA')}`;

  const getPlaceholder = () => {
    if (selected && selected <= 6) {
      return t('negativePHBloomer', { ns: 'NPSMission' });
    } else {
      return t('placeholder', { ns: 'NPSMission' });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        flexFlow: 'row nowrap',
        alignItems: 'center',
      }}
    >
      {!CRAValidated && (
        <div>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ActionButton
              id="validateOrCancelCRA"
              loading={modalState.loading.show}
              emoji={<SignCRAIcon />}
              disable={modalState.nps.show || modalState.craActions.show}
              onClick={async () => {
                if (isZeroCRAActivity(activity)) {
                  setModalState({ ...modalState, craActions: { id: activity.recordId, show: true } });
                  return;
                }
                const cancelLoadingId = setTimeout(() => {
                  setModalState({ ...modalState, loading: { id: i, show: true } });
                });
                const NPS = await getNPS({
                  variables: {
                    month: activity.date,
                    type: 'bloomer',
                    activityId: activity.recordId,
                  },
                });
                clearTimeout(cancelLoadingId);
                setModalState({ ...modalState, loading: { id: i, show: false } });
                if (NPS?.data?.getMissionFollowUp.score !== null) {
                  setModalState({ ...modalState, craActions: { id: activity.recordId, show: true } });
                } else {
                  setModalState({
                    ...modalState,
                    nps: { id: i, show: true },
                    craActions: { ...modalState.craActions, id: activity.recordId },
                  });
                }
              }}
              textButton={labelCRAButton}
            />
            {CRAEditing && <Button color="secondary" onClick={editFunc} textButton={t('editCRA')} />}
          </Box>
        </div>
      )}
      <Modal
        open={modalState.nps.show}
        onClose={handleClose}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
          overflow: 'scroll',
          [theme.breakpoints.down('md')]: {
            padding: '16px',
          },
        }}
      >
        <NPSMission
          audience="bloomer"
          stateNPS={stateNPS}
          onClick={async () => {
            await upsertFollowUp({
              comment,
              selected,
              collectDate: activity.date,
              activityId: activity.recordId!,
            });
            setModalState((prevState) => ({
              ...prevState,
              craActions: { id: activity.recordId, show: true },
              nps: { id: prevState.nps.id, show: false },
            }));
          }}
          setSelected={(e: number) => setSelected(e)}
          setComment={(e: any) => setComment(e.target.value)}
          selected={selected}
          comment={comment}
          texts={{
            title: mission.enableCRAUpload
              ? t('uploadTitle', { ns: 'NPSMission' })
              : t('signTitle', { ns: 'NPSMission' }),
            subtitle: t('subtitle', { ns: 'NPSMission' }),
            placeholder: getPlaceholder(),
            button: mission.enableCRAUpload
              ? t('buttonUpload', { ns: 'NPSMission' })
              : t('buttonSign', { ns: 'NPSMission' }),
            details: t('details', { ns: 'NPSMission' }),
            nameLink: 'BloomHelp',
            url: 'https://bit.ly/bloomays-help',
            btnTitle: t('thanksToNote', { ns: 'NPSMission' }),
          }}
          showBusinessReferal={mission.enableBusinessReferalNpsBloomer}
        />
      </Modal>
      <Modal
        open={!!nbDaysDetails}
        onClose={() => setNbDaysDetails(undefined)}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          height: '90%',
          border: 'none',
          borderRadius: '20px',
          overflow: 'scroll',
        }}
      >
        {nbDaysDetails && (
          <>
            <SummaryDaysWorked
              month={nbDaysDetails.month}
              daysWorked={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysWorked)}
              daysMarkup={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysMarkupWorked)}
              daysMarkup2={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.nbDaysMarkup2Worked)}
              daysAstreinte={calculPerDay(nbDaysDetails.nbDaysDetails, ActivityDayType.astreinte)}
            />
            <FormCRA
              eachDays={nbDaysDetails.nbDaysDetails || []}
              readOnly={true}
              onCRAUpdate={() => void 0}
              hoursPerDay={nbDaysDetails.hoursPerDays}
            />
          </>
        )}
      </Modal>
      <Modal
        open={modalState.craActions.show}
        onClose={handleCloseAction}
        additionnalCSS={{
          width: '700px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        <ActionCRA
          handleCloseAction={handleCloseAction}
          refetchActivities={refetchActivities}
          CRASignSent={CRASignSent}
          cancelFunc={cancelFunc}
          CRACanSendInvite={CRACanSendInvite}
          sendFunc={sendFunc}
          CRAUploaded={CRAUploaded}
          uploadCRA={uploadCRA}
          activity={activity}
          mission={mission}
          isZeroCRA={isZeroCRAActivity(activity)}
        />
      </Modal>
      <Modal
        open={modalState.errorBillingInfos.show}
        onClose={() => {
          setModalState({ ...modalState, errorBillingInfos: { show: false, fields: [] } });
        }}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        <ErrorBillingInfosTitle>
          <IconBox>
            <WarningIcon />
          </IconBox>
          <h4>{t('errorBillingInfosTitle')}</h4>
        </ErrorBillingInfosTitle>
        <ErrorBillingInfosDescription>
          <p>{t('errorBillingInfosDescription')}</p>
          <ul>
            {modalState.errorBillingInfos.fields &&
              modalState.errorBillingInfos.fields.map((field, index) => (
                <li key={index}>
                  <span role="img" aria-label="warning">
                    ⚠️
                  </span>{' '}
                  {field}
                </li>
              ))}
          </ul>
          {!isAdmin ? (
            modalState.errorBillingInfos.fields.includes(t('missingBillingSociety')) ? (
              <HrefLink href={`/society/new/mission/${mission.recordId}`} text={t('createBillingSociety')} />
            ) : (
              <HrefLink
                href={
                  !isEmpty(mission.bloomerBillingSociety)
                    ? `/society/${mission.bloomerBillingSociety![0].recordId}/mission/${mission.recordId}`
                    : ''
                }
                text={t('modifyMyBillingInfos')}
              />
            )
          ) : (
            <HrefLink href={`/missions/${mission.recordId}`} text={t('modifyMyBillingInfos')} />
          )}
        </ErrorBillingInfosDescription>
      </Modal>
      {!CRAEditing && !CRASignSent && (
        <Box>
          <ContainerInvoices>
            {canUploadInvoices ? (
              <ActionButton emoji={<UploadFileIcon />} onClick={handleUploadInvoice} textButton={t('uploadInvoice')} />
            ) : (
              <>
                {canSeeInvoices && activity.urlInvoicesS3 && (
                  <>
                    <ActionButton
                      emoji={<RawVisibilityIcon />}
                      title={t('seeInvoice')}
                      textButton={t('seeInvoice')}
                      onClick={() => window.open(activity.urlInvoicesS3, '_blank')}
                    />
                    {canDeleteInvoices && activity.urlInvoicesS3 && !activity.payed && (
                      <ActionButton
                        emoji={<Delete />}
                        loading={loadingDelete}
                        title={t('deleteInvoice')}
                        textButton={t('deleteInvoice')}
                        onClick={async () => {
                          setTimeout(() => {
                            setLoadingDelete(true);
                          });
                          await deleteInvoice(activity.recordId).catch((err) => {
                            logger.error(err);
                          });
                          setLoadingDelete(false);
                        }}
                      />
                    )}
                  </>
                )}
                {canSeeInvoices && activity.urlAdditionalInvoicesS3 && (
                  <ActionButton
                    emoji={<RawVisibilityIcon />}
                    title={t('seeAdditionalInvoice')}
                    textButton={t('seeAdditionalInvoice')}
                    onClick={() => window.open(activity.urlAdditionalInvoicesS3, '_blank')}
                  />
                )}
              </>
            )}
            {activity?.urlCraS3 && (
              <ActionButton
                title={t('seeCRA')}
                onClick={() => window.open(activity.urlCraS3, '_blank')}
                textButton={t('seeCRA')}
              />
            )}
            {!activity.urlCraS3 && activity.validated && (
              <ActionButton
                title={t('seeCRA')}
                onClick={() => {
                  const isoDate = new Date(activity.date).toISOString();
                  const eachDays = convertActivityToEachDaysArray(isoDate, activity.nbDaysDetails);
                  setNbDaysDetails({
                    nbDaysDetails: eachDays,
                    hoursPerDays: activity.hoursPerDays,
                    month: isoDate,
                  });
                }}
                textButton={t('seeCRA')}
              />
            )}
          </ContainerInvoices>
        </Box>
      )}
    </Box>
  );
};

const ActionButton = styled(Button)(({ theme }) => ({
  marginBottom: '2px',
}));

const SignCRAIcon = styled(RawInsertDriveFileIcon)(({ theme }) => ({
  paddingRight: '5px',
  width: '19px !important',
}));

const UploadFileIcon = styled(RawUploadFileIcon)(({ theme }) => ({
  paddingRight: '5px',
  width: '19px !important',
}));

const ContainerInvoices = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
}));

const IconBox = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.gradient.red[20],
  width: '40px',
  height: '40px',
  borderRadius: '5px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const WarningIcon = styled(RawWarningIcon)(({ theme }) => ({
  color: theme.palette.red[60],
  width: 20,
}));

const ErrorBillingInfosTitle = styled('h3')(() => ({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
}));

const ErrorBillingInfosDescription = styled('div')(() => ({
  textAlign: 'left',
  marginTop: '10px',
  ul: {
    margin: '10px 0',
  },
}));
